// Client-side funnel instrumentation (RP-E15).
//
// Reports the steps only the browser can observe — landing, game detail,
// launch, first frame, five minutes played, netplay connected — to
// POST /api/funnel. Invite and completion steps are emitted server-side
// instead; see src/server/routes/funnel.js for why.
//
// Imported by BOTH the main client bundle and the /library bundle, so it
// deliberately has no dependencies: the two apps load feature flags in
// different ways (moduleRegistry's cached singleton vs a per-boot fetch), so
// each one calls configureFunnel() once it knows, rather than this module
// reaching into either. That also keeps it a few hundred bytes in the entry
// bundle, which is close to its size budget.
//
// Four properties this module has to hold:
//
//   1. It can never break gameplay. Every send is fire-and-forget with a
//      swallowed rejection, and no caller ever awaits one.
//   2. It fails CLOSED. Until a host app says collection is enabled, nothing
//      is sent. An admin who kills the flag, or a boot where flags never
//      loaded, produces no events — the opposite of the fail-open default
//      moduleRegistry.isEnabled() uses for non-destructive features.
//   3. Steps fired before the flag resolves are not lost. `landing` happens at
//      boot, before flags arrive, so early steps buffer and are either flushed
//      or discarded once configureFunnel() settles the question.
//   4. It sends nothing sensitive. The payload is built from a closed set of
//      fields and validated again server-side; no ROM name, hash, title, or
//      free text ever goes in. Private/BYO launches send `private_rom: true`
//      instead of a game id.
//
// The correlation id lives in memory only. That keeps same-page funnel steps
// correlated without writing a non-essential analytics identifier to the
// visitor's device before cookie/storage consent. A hard navigation starts a
// new id; the privacy tradeoff is deliberate.

const ENDPOINT = '/api/funnel';

// null = not yet known (buffer), true = collect, false = drop.
let _enabled = null;
let _funnelId = null;
// Steps already reported for this page lifetime. The server dedupes too, but
// suppressing here saves the request entirely — most steps are
// once-per-session by definition.
const _sent = new Set();
// Steps fired before the flag resolved. Bounded so a boot that never calls
// configureFunnel() can't grow it without limit.
const MAX_PENDING = 20;
let _pending = [];

/**
 * Tell the module whether collection is on. Call once per page as soon as
 * feature flags are known; safe to call again on a mid-session flag refresh.
 *
 * Until this is called, steps buffer rather than send. Enabling flushes the
 * buffer; disabling discards it.
 */
export function configureFunnel({ enabled }) {
  _enabled = enabled === true;
  const queued = _pending;
  _pending = [];
  if (!_enabled) return;
  for (const body of queued) send(body);
}

function randomId() {
  try {
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  // Pre-randomUUID Safari and any locked-down context. Math.random is fine
  // here: this id only has to be unlikely to collide within a day's traffic,
  // it is not a secret and never authorizes anything.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

/**
 * The page-scoped correlation id, minted on first use. It deliberately is not
 * persisted to cookies, localStorage, sessionStorage, or IndexedDB.
 */
export function funnelId() {
  if (_funnelId) return _funnelId;
  _funnelId = randomId();
  return _funnelId;
}

function send(body) {
  // keepalive so a step fired as the user navigates away (library detail →
  // launch on / ) still reaches the server.
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => { /* instrumentation never surfaces errors */ });
}

/**
 * Report a funnel step. Fire-and-forget: returns nothing, throws nothing, and
 * must never be awaited by a gameplay path.
 *
 * @param {string} step One of the client-emitted steps in src/shared/funnel-events.js.
 * @param {object} [fields] Optional context — room_code, system_id, net_mode,
 *   surface, detail. `repeat: true` opts a step out of the once-per-page dedupe.
 *   Anything else is ignored, here and server-side. Note there is deliberately no
 *   game reference: the server refuses a client-supplied `game_id` because only
 *   an in-process caller can prove the row is catalog-linked rather than a stub
 *   keyed on a private ROM's hash.
 */
export function trackFunnelStep(step, fields = {}) {
  try {
    if (_enabled === false) return;

    const dedupeKey = `${step}:${fields.room_code || ''}`;
    if (!fields.repeat && _sent.has(dedupeKey)) return;
    _sent.add(dedupeKey);

    // event_id + event_time identify this exact occurrence. They stay stable
    // if the browser/network retries the POST, while a deliberate second
    // launch gets a fresh pair. The server's unique (time,event_id) index is
    // the cross-replica idempotency boundary; funnel_id remains the separate
    // page-lifetime correlation key used by the activation report.
    const body = {
      step,
      event_id: randomId(),
      event_time: new Date().toISOString(),
      funnel_id: funnelId(),
    };
    // Copy only the allowlisted context fields, skipping empty values so the
    // payload stays minimal and the server stores NULL rather than ''.
    for (const key of ['room_code', 'system_id', 'net_mode', 'surface', 'detail']) {
      if (fields[key]) body[key] = fields[key];
    }

    if (_enabled === null) {
      // Flags haven't arrived yet. Buffer so boot-time steps survive, but drop
      // the oldest past the cap rather than growing without bound.
      if (_pending.length < MAX_PENDING) _pending.push(body);
      return;
    }
    send(body);
  } catch {
    /* never let a tracking bug reach the caller */
  }
}

/**
 * Clear the once-per-page dedupe for the per-launch steps so the next launch
 * reports them again. A visitor who plays, quits to the menu, and launches
 * something else has genuinely launched twice, and those steps must not be
 * suppressed as duplicates of the first session.
 */
export function resetFunnelSession() {
  for (const key of [..._sent]) {
    if (key.startsWith('launch_requested') || key.startsWith('first_frame')
      || key.startsWith('play_5min') || key.startsWith('net_first_frame')
      || key.startsWith('connected')) {
      _sent.delete(key);
    }
  }
}

/** Test seam — drops the cached id, the dedupe set, and the pending buffer. */
export function _resetFunnelClientState() {
  _enabled = null;
  _funnelId = null;
  _sent.clear();
  _pending = [];
}
